import React, { useState } from "react";
import { Lock, Plus, X } from "lucide-react";
import EquityLedger from "./EquityLedger";
import NoteLedger from "./NoteLedger";
import MobilityLedger from "./MobilityLedger";
import CryptoLedger from "./CryptoLedger";
import ThirdPartyLedger from "./ThirdPartyLedger";
import CurrencyLedger from "./CurrencyLedger";
export { holdingValue } from "../lib/holdingsMath";

export const SEEDERS = [
  ["equities", "Equities", false],
  ["long_note", "Long note", false],
  ["short_note", "Short note", false],
  ["mobility", "Mobility property", false],
  ["real_estate", "Real estate", false],
  ["crypto", "Crypto / NFTs", false],
  ["third_party", "3rd party fund", false],
  ["register", "Transaction register", false],
];
export const SEEDER_LABEL = Object.fromEntries(SEEDERS.map(([k, l]) => [k, l]));
export const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function renderSeederLedger(seeder, { scopeCategory, block, item, filtered, reload }) {
  const scope = { category: scopeCategory, alt_block: block, alt_item: item, seeder };
  const pfx = `sd-${slug(block)}-${slug(item)}-${seeder}`;
  switch (seeder) {
    case "equities": return <EquityLedger sector={item} positions={filtered} reload={reload} createScope={scope} />;
    case "long_note": return <NoteLedger sector={item} forceType="long" notes={filtered} reload={reload} scope={scope} />;
    case "short_note": return <NoteLedger sector={item} forceType="short" notes={filtered} reload={reload} scope={scope} />;
    case "mobility": return <MobilityLedger items={filtered} reload={reload} category={scopeCategory} scope={scope} p={pfx} />;
    case "real_estate": return <MobilityLedger items={filtered} reload={reload} category={scopeCategory} class2Options={["AirBnB", "Hotel", "Multifamily", "Resort", "VRBO"]} intents={["Sell", "Hold", "Develop", "Lease", "Run"]} madeLabel="Built" scope={scope} p={pfx} />;
    case "crypto": return <CryptoLedger notes={filtered} reload={reload} scope={scope} />;
    case "third_party": return <ThirdPartyLedger items={filtered} reload={reload} scope={scope} />;
    case "register": return <CurrencyLedger app="box" accountId={`${slug(block)}-${slug(item)}`} regName={`${item} — Register`} />;
    default: return null;
  }
}

// Multi-seeder container. `loaded` (array) + onAdd/onRemove are owned by the parent (persistence).
export default function SeederHost({ scopeCategory, block, item, items = [], reload, loaded = [], onAdd, onRemove, tp }) {
  const [pending, setPending] = useState("");
  const itemsFor = items.filter((h) => h.alt_block === block && h.alt_item === item);
  const forSeeder = (s) => itemsFor.filter((h) => h.seeder === s);
  const available = SEEDERS.filter(([k]) => !loaded.includes(k));
  const prefix = tp || `sh-${slug(block)}-${slug(item)}`;

  return (
    <div className="space-y-3" data-testid={`${prefix}-host`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[9px] uppercase tracking-wide opacity-50">Add seeder</span>
        <select value={pending} onChange={(e) => setPending(e.target.value)} data-testid={`${prefix}-select`}
          className="rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[11px] dark:border-white/15 dark:bg-black/30">
          <option value="">— choose format —</option>
          {available.map(([k, l, dis]) => <option key={k} value={k} disabled={dis}>{l}</option>)}
        </select>
        <button type="button" onClick={() => { if (pending) { onAdd(pending); setPending(""); } }} disabled={!pending} data-testid={`${prefix}-add`}
          className="flex items-center gap-1 rounded bg-[var(--primary)] px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-40"><Plus size={12} /> Load</button>
      </div>

      {loaded.length === 0 && <div className="py-3 text-center text-[11px] opacity-55">No seeders loaded yet. Choose one or more formats above to start entering data.</div>}

      {loaded.map((seeder) => {
        const sCount = forSeeder(seeder).length;
        const locked = sCount > 0;
        return (
          <div key={seeder} className="rounded-lg border border-black/10 bg-white/40 p-2 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`${prefix}-panel-${seeder}`}>
            <div className="mb-2 flex items-center gap-2">
              <span className="rounded bg-[var(--primary)]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[var(--primary)]">{SEEDER_LABEL[seeder]}</span>
              {sCount > 0 && <span className="text-[10px] opacity-55">{sCount} record(s)</span>}
              <button type="button" onClick={() => { if (!locked) onRemove(seeder); }} disabled={locked} title={locked ? "Remove blocked — this seeder holds records" : "Remove seeder"} data-testid={`${prefix}-remove-${seeder}`}
                className="ms-auto flex items-center gap-0.5 text-[10px] opacity-60 hover:text-red-500 hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-40">
                {locked ? <Lock size={11} /> : <X size={12} />}{locked ? "locked" : "remove"}
              </button>
            </div>
            {renderSeederLedger(seeder, { scopeCategory, block, item, filtered: forSeeder(seeder), reload })}
          </div>
        );
      })}
    </div>
  );
}
